import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, Calendar, Ruler, Weight, Activity, Info, Building2, Users, Anchor, Plus, Trash2, UserPlus, Accessibility, Scale } from 'lucide-react';
import TopNav from '@/components/TopNav';
import { WEIGHT_CATEGORIES } from '@/lib/boatCalculations';
import { validateHrSettings, DEFAULT_REST_HR, DEFAULT_MAX_HR } from '@/lib/hrZones';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Calculate age from date of birth
const calculateAge = (dob) => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Calculate Max HR using formula: HRmax = 208 - (0.7 × Age)
const calculateMaxHR = (age) => {
  if (!age || age < 1) return null;
  return Math.round(208 - (0.7 * age));
};

export default function ProfileSetup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [calculatedMaxHR, setCalculatedMaxHR] = useState(null);
  const [userAge, setUserAge] = useState(null);
  const [formData, setFormData] = useState({
    dob: '',
    sex: '',
    height: '',
    weight: '',
    resting_hr: '',
    max_hr: '',
    club: '',
    weight_category: 'men',
    weight_class: 'openweight',           // openweight | lightweight (D-048)
    para_classification: 'none',          // none | PR1 | PR2 | PR3 (D-048)
    is_coxswain: false,
    crew_weight_mode: 'category', // 'category', 'average', or 'roster'
    crew_avg_weight: '',
    crew_roster: [] // Array of { name: string, weight: number }
  });
  
  // For adding new crew members
  const [newCrewName, setNewCrewName] = useState('');
  const [newCrewWeight, setNewCrewWeight] = useState('');

  useEffect(() => {
    fetch(`${API}/user/profile`, { credentials: 'include' })
      .then(res => res.json())
      .then(user => {
        setFormData({
          dob: user.dob || '',
          sex: user.sex || '',
          height: user.height || '',
          weight: user.weight || '',
          resting_hr: user.hr_rest_bpm ?? user.resting_hr ?? '',
          max_hr: user.hr_max_setting_bpm ?? user.max_hr ?? '',
          club: user.club || '',
          weight_category: user.weight_category || 'men',
          weight_class: user.weight_class || 'openweight',
          para_classification: user.para_classification || 'none',
          is_coxswain: user.is_coxswain || false,
          crew_weight_mode: user.crew_weight_mode || 'category',
          crew_avg_weight: user.crew_avg_weight || '',
          crew_roster: user.crew_roster || []
        });
        // Calculate age and max HR if DOB exists
        if (user.dob) {
          const age = calculateAge(user.dob);
          setUserAge(age);
          setCalculatedMaxHR(calculateMaxHR(age));
        }
      })
      .catch(err => console.error('Failed to load profile:', err));
  }, []);

  // Update calculated Max HR when DOB changes
  const handleDobChange = (newDob) => {
    setFormData({ ...formData, dob: newDob });
    const age = calculateAge(newDob);
    setUserAge(age);
    const maxHR = calculateMaxHR(age);
    setCalculatedMaxHR(maxHR);
  };

  // Add crew member to roster
  const addCrewMember = () => {
    if (!newCrewName.trim() || !newCrewWeight) {
      toast.error('Please enter both name and weight');
      return;
    }
    const weight = parseFloat(newCrewWeight);
    if (isNaN(weight) || weight <= 0) {
      toast.error('Please enter a valid weight');
      return;
    }
    setFormData(prev => ({
      ...prev,
      crew_roster: [...prev.crew_roster, { name: newCrewName.trim(), weight }]
    }));
    setNewCrewName('');
    setNewCrewWeight('');
  };

  // Remove crew member from roster
  const removeCrewMember = (index) => {
    setFormData(prev => ({
      ...prev,
      crew_roster: prev.crew_roster.filter((_, i) => i !== index)
    }));
  };

  // Calculate average weight from roster
  const getRosterAverageWeight = () => {
    if (formData.crew_roster.length === 0) return null;
    const total = formData.crew_roster.reduce((sum, member) => sum + member.weight, 0);
    return (total / formData.crew_roster.length).toFixed(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const restHr = formData.resting_hr === '' ? DEFAULT_REST_HR : parseInt(formData.resting_hr, 10);
    const ownMaxHr = formData.max_hr === '' ? null : parseInt(formData.max_hr, 10);
    const hrError = validateHrSettings({ rest: restHr, max: ownMaxHr, dob: formData.dob });
    if (hrError) {
      toast.error(hrError);
      return;
    }
    setLoading(true);

    // Determine the effective crew weight based on mode
    let effectiveCrewWeight = null;
    if (formData.is_coxswain) {
      if (formData.crew_weight_mode === 'roster' && formData.crew_roster.length > 0) {
        effectiveCrewWeight = parseFloat(getRosterAverageWeight());
      } else if (formData.crew_weight_mode === 'average' && formData.crew_avg_weight) {
        effectiveCrewWeight = parseFloat(formData.crew_avg_weight);
      }
    }

    try {
      const response = await fetch(`${API}/user/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          dob: formData.dob,
          sex: formData.sex,
          height: parseFloat(formData.height),
          weight: parseFloat(formData.weight),
          hr_rest_bpm: restHr,
          hr_max_setting_bpm: ownMaxHr,
          club: formData.club || null,
          weight_category: formData.weight_category,
          weight_class: formData.weight_class,
          para_classification: formData.para_classification,
          is_coxswain: formData.is_coxswain,
          crew_weight_mode: formData.crew_weight_mode,
          crew_avg_weight: effectiveCrewWeight,
          crew_roster: formData.is_coxswain ? formData.crew_roster : []
        })
      });

      if (!response.ok) throw new Error('Failed to update profile');

      toast.success('Profile updated successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to update profile');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-900 flex flex-col transition-colors">
      <TopNav />
      <div className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tighter mb-2 text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
              Complete Your Profile
            </h1>
            <p className="text-zinc-500 dark:text-zinc-400">Enter your biometric data for accurate training zones</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm p-6" data-testid="profile-form">
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                  <Calendar className="inline w-4 h-4 mr-2" />Date of Birth
                </label>
                <input
                  type="date"
                  data-testid="dob-input"
                  required
                  value={formData.dob}
                  onChange={(e) => handleDobChange(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none font-mono bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                />
                {userAge && (
                  <p className="text-xs text-zinc-400 mt-1">Age: {userAge} years</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                  <User className="inline w-4 h-4 mr-2" />Sex
                </label>
                <select
                  data-testid="sex-select"
                  required
                  value={formData.sex}
                  onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none font-mono bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                    <Ruler className="inline w-4 h-4 mr-2" />Height (cm)
                  </label>
                  <input
                    type="number"
                    data-testid="height-input"
                    required
                    step="0.1"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none font-mono bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                    <Weight className="inline w-4 h-4 mr-2" />Weight (kg)
                  </label>
                  <input
                    type="number"
                    data-testid="weight-input"
                    required
                    step="0.1"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none font-mono bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                    <Activity className="inline w-4 h-4 mr-2" />Resting HR
                  </label>
                  <input
                    type="number"
                    data-testid="resting-hr-input"
                    min={30}
                    max={100}
                    value={formData.resting_hr}
                    onChange={(e) => setFormData({ ...formData, resting_hr: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none font-mono bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                    placeholder="60"
                  />
                  <p className="text-xs text-blue-500 mt-1 flex items-start gap-1" data-testid="resting-hr-prompt">
                    <Info className="w-3 h-3 mt-0.5 shrink-0" />
                    Set your resting HR for accurate zones. Measure it on waking, lying down.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                    <Activity className="inline w-4 h-4 mr-2" />Max HR (optional)
                  </label>
                  <input
                    type="number"
                    data-testid="max-hr-input"
                    min={120}
                    max={230}
                    value={formData.max_hr}
                    onChange={(e) => setFormData({ ...formData, max_hr: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none font-mono bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                    placeholder={`Auto: ${calculatedMaxHR || DEFAULT_MAX_HR}`}
                  />
                  {!formData.max_hr && (
                    <p className="text-xs text-blue-500 mt-1 flex items-center gap-1" data-testid="max-hr-fallback">
                      <Info className="w-3 h-3" />
                      {calculatedMaxHR
                        ? `Will use ${calculatedMaxHR} BPM (208 − 0.7 × ${userAge})`
                        : `Will use ${DEFAULT_MAX_HR} BPM (add your date of birth for an age estimate)`}
                    </p>
                  )}
                  {calculatedMaxHR && formData.max_hr && parseInt(formData.max_hr) !== calculatedMaxHR && (
                    <p className="text-xs text-zinc-400 mt-1">
                      Calculated: {calculatedMaxHR} BPM
                    </p>
                  )}
                </div>
              </div>

              {/* Club Section */}
              <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700">
                <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                  <Building2 className="inline w-4 h-4 mr-2" />Rowing Club (Optional)
                </label>
                <input
                  type="text"
                  data-testid="club-input"
                  value={formData.club}
                  onChange={(e) => setFormData({ ...formData, club: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                  placeholder="e.g. Oxford University Boat Club"
                />
                <p className="text-xs text-zinc-400 mt-1">
                  Your rowing club or team affiliation
                </p>
              </div>

              {/* Weight Class (openweight / lightweight) — CrewZ v1.6 D-048.
                  Feeds the live World Best Time crown comparison. */}
              <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                    <Scale className="inline w-4 h-4 mr-2" />Weight Class
                  </label>
                  <select
                    data-testid="weight-class-select"
                    value={formData.weight_class}
                    onChange={(e) => setFormData({ ...formData, weight_class: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none font-mono bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                  >
                    <option value="openweight">Openweight</option>
                    <option value="lightweight">Lightweight</option>
                  </select>
                  <p className="text-xs text-zinc-400 mt-1">
                    Used for World Best Time comparisons
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                    <Accessibility className="inline w-4 h-4 mr-2" />Para Classification
                  </label>
                  <select
                    data-testid="para-classification-select"
                    value={formData.para_classification}
                    onChange={(e) => setFormData({ ...formData, para_classification: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none font-mono bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                  >
                    <option value="none">None</option>
                    <option value="PR1">PR1</option>
                    <option value="PR2">PR2</option>
                    <option value="PR3">PR3</option>
                  </select>
                  <p className="text-xs text-zinc-400 mt-1">
                    Functional classification (World Rowing Para)
                  </p>
                </div>
              </div>

              {/* Coxswain Section - Contains Weight Category, Crew Average, and Roster */}
              <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700">
                <div className="flex items-center gap-3 mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="is-coxswain-checkbox"
                      checked={formData.is_coxswain}
                      onChange={(e) => setFormData({ ...formData, is_coxswain: e.target.checked })}
                      className="w-5 h-5 rounded border-2 border-zinc-300 dark:border-zinc-600 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 flex items-center gap-2">
                      <Anchor className="w-4 h-4" />
                      I am a Coxswain
                    </span>
                  </label>
                </div>
                
                {formData.is_coxswain ? (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg space-y-4">
                    <div>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                        <Info className="inline w-4 h-4 mr-1" />
                        As a coxswain, your weight isn&apos;t used in power calculations. Choose how to calculate your crew&apos;s average weight:
                      </p>
                      
                      {/* Weight Mode Selector */}
                      <div className="flex gap-2 mb-4">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, crew_weight_mode: 'category' })}
                          className={`flex-1 px-3 py-2 text-xs font-bold rounded-lg transition-colors ${
                            formData.crew_weight_mode === 'category'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 border border-blue-200 dark:border-zinc-600'
                          }`}
                        >
                          <Users className="w-4 h-4 mx-auto mb-1" />
                          Weight Category
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, crew_weight_mode: 'average' })}
                          className={`flex-1 px-3 py-2 text-xs font-bold rounded-lg transition-colors ${
                            formData.crew_weight_mode === 'average'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 border border-blue-200 dark:border-zinc-600'
                          }`}
                        >
                          <Weight className="w-4 h-4 mx-auto mb-1" />
                          Manual Average
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, crew_weight_mode: 'roster' })}
                          className={`flex-1 px-3 py-2 text-xs font-bold rounded-lg transition-colors ${
                            formData.crew_weight_mode === 'roster'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 border border-blue-200 dark:border-zinc-600'
                          }`}
                        >
                          <UserPlus className="w-4 h-4 mx-auto mb-1" />
                          Crew Roster
                        </button>
                      </div>
                    </div>
                    
                    {/* Weight Category Option */}
                    {formData.crew_weight_mode === 'category' && (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-blue-600 dark:text-blue-400">
                          <Users className="inline w-4 h-4 mr-2" />Weight Category
                        </label>
                        <select
                          data-testid="weight-category-select"
                          value={formData.weight_category}
                          onChange={(e) => setFormData({ ...formData, weight_category: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-blue-200 dark:border-blue-700 rounded-lg focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none bg-white dark:bg-zinc-800 dark:text-white"
                        >
                          {Object.entries(WEIGHT_CATEGORIES).map(([key, cat]) => (
                            <option key={key} value={key}>
                              {cat.label} (avg {cat.avgWeight}kg)
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                          Uses standard weight averages for the selected category
                        </p>
                      </div>
                    )}
                    
                    {/* Manual Average Option */}
                    {formData.crew_weight_mode === 'average' && (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-blue-600 dark:text-blue-400">
                          <Weight className="inline w-4 h-4 mr-2" />Average Crew Weight (kg)
                        </label>
                        <input
                          type="number"
                          data-testid="crew-avg-weight-input"
                          step="0.1"
                          value={formData.crew_avg_weight}
                          onChange={(e) => setFormData({ ...formData, crew_avg_weight: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-blue-200 dark:border-blue-700 rounded-lg focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none bg-white dark:bg-zinc-800 dark:text-white"
                          placeholder="e.g. 85"
                        />
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                          Enter the average weight of your crew members
                        </p>
                      </div>
                    )}
                    
                    {/* Crew Roster Option */}
                    {formData.crew_weight_mode === 'roster' && (
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-blue-600 dark:text-blue-400">
                          <UserPlus className="inline w-4 h-4 mr-2" />Crew Roster
                        </label>
                        
                        {/* Crew List */}
                        {formData.crew_roster.length > 0 && (
                          <div className="mb-3 space-y-2">
                            {formData.crew_roster.map((member, index) => (
                              <div key={index} className="flex items-center gap-2 bg-white dark:bg-zinc-800 p-2 rounded-lg border border-blue-200 dark:border-blue-700">
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{member.name}</span>
                                  <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-2">{member.weight}kg</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeCrewMember(index)}
                                  className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            
                            {/* Average calculated from roster */}
                            <div className="bg-blue-100 dark:bg-blue-800/50 p-2 rounded-lg text-center">
                              <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
                                Calculated Average: {getRosterAverageWeight()}kg ({formData.crew_roster.length} crew)
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Add New Crew Member */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Name"
                            value={newCrewName}
                            onChange={(e) => setNewCrewName(e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border-2 border-blue-200 dark:border-blue-700 rounded-lg focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none bg-white dark:bg-zinc-800 dark:text-white"
                          />
                          <input
                            type="number"
                            placeholder="Weight (kg)"
                            step="0.1"
                            value={newCrewWeight}
                            onChange={(e) => setNewCrewWeight(e.target.value)}
                            className="w-24 px-3 py-2 text-sm border-2 border-blue-200 dark:border-blue-700 rounded-lg focus:border-blue-400 dark:focus:border-blue-500 focus:outline-none bg-white dark:bg-zinc-800 dark:text-white"
                          />
                          <button
                            type="button"
                            onClick={addCrewMember}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                          Add each crew member to calculate the exact average weight
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Non-coxswain Weight Category */
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2 text-zinc-500 dark:text-zinc-400">
                      <Users className="inline w-4 h-4 mr-2" />Weight Category
                    </label>
                    <select
                      data-testid="weight-category-select"
                      value={formData.weight_category}
                      onChange={(e) => setFormData({ ...formData, weight_category: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-zinc-200 dark:border-zinc-600 rounded-lg focus:border-zinc-400 dark:focus:border-zinc-500 focus:outline-none bg-zinc-50 dark:bg-zinc-700 dark:text-white"
                    >
                      {Object.entries(WEIGHT_CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>
                          {cat.label} (avg {cat.avgWeight}kg)
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-zinc-400 mt-1">
                      Used for power calculations in crew boats - assumes teammates have similar weight
                    </p>
                  </div>
                )}
              </div>

              {/* Formula explanation */}
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                <p className="text-blue-800 dark:text-blue-300">
                  <Info className="inline w-4 h-4 mr-1" />
                  <strong>Max HR Formula:</strong> If not provided, Max HR is calculated using <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">208 - (0.7 × Age)</code>, 
                  which is more accurate across all adult age groups.
                </p>
              </div>
            </div>

            <button
              type="submit"
              data-testid="save-profile-button"
              disabled={loading}
              className="w-full mt-6 px-8 py-4 text-lg font-bold uppercase tracking-wider bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save & Continue'}
            </button>
          </form>

          {/* General Information - Athletes Energy Systems */}
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4 text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
              Training Zones Reference
            </h2>
            <div className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800">
                <h3 className="font-bold text-lg text-zinc-900 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
                  Athletes Energy Systems
                </h3>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Training zones based on heart rate percentage</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 dark:bg-zinc-800 border-b border-zinc-200 dark:border-zinc-700">
                      <th className="px-4 py-3 text-left font-bold text-zinc-600 dark:text-zinc-300">Zone</th>
                      <th className="px-4 py-3 text-left font-bold text-zinc-600 dark:text-zinc-300">Colour</th>
                      <th className="px-4 py-3 text-left font-bold text-zinc-600 dark:text-zinc-300">Full Name</th>
                      <th className="px-4 py-3 text-left font-bold text-zinc-600 dark:text-zinc-300">Primary Energy System</th>
                      <th className="px-4 py-3 text-left font-bold text-zinc-600 dark:text-zinc-300">Heart Rate (% HR)</th>
                      <th className="px-4 py-3 text-left font-bold text-zinc-600 dark:text-zinc-300">Stroke Rate (SPM)</th>
                      <th className="px-4 py-3 text-left font-bold text-zinc-600 dark:text-zinc-300">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="text-zinc-700 dark:text-zinc-300">
                    <tr className="border-b border-zinc-100 dark:border-zinc-700">
                      <td className="px-4 py-3 font-bold">Utilization 3</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-gray-400"></span>
                          Grey
                        </span>
                      </td>
                      <td className="px-4 py-3 text-zinc-400">Recovery</td>
                      <td className="px-4 py-3 text-zinc-400">—</td>
                      <td className="px-4 py-3 text-zinc-400">&lt;55%</td>
                      <td className="px-4 py-3 text-zinc-400">—</td>
                      <td className="px-4 py-3 text-zinc-400">Recovery and regeneration</td>
                    </tr>
                    <tr className="border-b border-zinc-100 dark:border-zinc-700 bg-blue-50/30 dark:bg-blue-900/20">
                      <td className="px-4 py-3 font-bold">Utilization 2</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-blue-500"></span>
                          Blue
                        </span>
                      </td>
                      <td className="px-4 py-3">Oxygen Utilisation 2</td>
                      <td className="px-4 py-3">Aerobic (Low Intensity)</td>
                      <td className="px-4 py-3 font-mono">55-70%</td>
                      <td className="px-4 py-3 font-mono">16-20</td>
                      <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">Building base, capillarisation, and fat metabolism.</td>
                    </tr>
                    <tr className="border-b border-zinc-100 dark:border-zinc-700 bg-green-50/30 dark:bg-green-900/20">
                      <td className="px-4 py-3 font-bold">Utilization 1</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-green-500"></span>
                          Green
                        </span>
                      </td>
                      <td className="px-4 py-3">Oxygen Utilisation 1</td>
                      <td className="px-4 py-3">Aerobic (Intensive)</td>
                      <td className="px-4 py-3 font-mono">70-80%</td>
                      <td className="px-4 py-3 font-mono">20-24</td>
                      <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">Aerobic power; bridging base fitness to threshold work.</td>
                    </tr>
                    <tr className="border-b border-zinc-100 dark:border-zinc-700 bg-yellow-50/30 dark:bg-yellow-900/20">
                      <td className="px-4 py-3 font-bold">Threshold</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-yellow-400"></span>
                          Yellow
                        </span>
                      </td>
                      <td className="px-4 py-3">Anaerobic Threshold</td>
                      <td className="px-4 py-3">Mix (Lactate Threshold)</td>
                      <td className="px-4 py-3 font-mono">80-85%</td>
                      <td className="px-4 py-3 font-mono">24-28</td>
                      <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">Improving the point where lactate begins to accumulate rapidly.</td>
                    </tr>
                    <tr className="border-b border-zinc-100 dark:border-zinc-700 bg-orange-50/30 dark:bg-orange-900/20">
                      <td className="px-4 py-3 font-bold">Transport</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-orange-500"></span>
                          Orange
                        </span>
                      </td>
                      <td className="px-4 py-3">Oxygen Transport</td>
                      <td className="px-4 py-3">Aerobic (Maximal/VO2 Max)</td>
                      <td className="px-4 py-3 font-mono">85-95%</td>
                      <td className="px-4 py-3 font-mono">28-32</td>
                      <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">Developing maximum oxygen uptake (VO2 Max).</td>
                    </tr>
                    <tr className="bg-red-50/30 dark:bg-red-900/20">
                      <td className="px-4 py-3 font-bold">Anaerobic</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-4 h-4 rounded-full bg-red-500"></span>
                          Red
                        </span>
                      </td>
                      <td className="px-4 py-3">Anaerobic</td>
                      <td className="px-4 py-3">Anaerobic (Glycolytic/ATP-PC)</td>
                      <td className="px-4 py-3 font-mono">95-100%</td>
                      <td className="px-4 py-3 font-mono">32+</td>
                      <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">Sprint speed, lactate tolerance, and peak power.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
